
import React, { useState, useEffect, useRef, memo } from 'react';
import { Link } from 'react-router-dom';
import config from '../Common/Configurations/APIConfig';
import apiService from '../Common/Configurations/APIService';
import DefaultData from '../CompanyData/DefaultData';
import { FaEye, FaArrowRight, FaMobileAlt, FaTachometerAlt, FaPaintBrush } from 'react-icons/fa';
import styles from './PortalSetUpSteps.module.css';

// ============= TEMPLATES DISPLAY COMPONENT (MEMOIZED) =============
const TemplatesDisplay = memo(({ onTemplateSelect }) => {
    const [activeType, setActiveType] = useState('B2C');
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [hoveredTemplate, setHoveredTemplate] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const scrollContainerRef = useRef(null);
    const thumbnailScrollRef = useRef(null);
    const isMounted = useRef(true);

    const TEMPLATE_TYPES = [
        { key: 'B2C', label: 'B2C Websites', icon: '🛒', description: 'Business to Customer - Perfect for travel agencies, tour operators', color: '#4F46E5' },
        { key: 'B2B', label: 'B2B Websites', icon: '🤝', description: 'Business to Business - Ideal for wholesalers, DMCs', color: '#10B981' },
        { key: 'B2E', label: 'B2E Websites', icon: '💼', description: 'Business to Employee - Corporate travel management', color: '#F59E0B' }
    ];

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        fetchTemplatesByType(activeType);
    }, [activeType]);

    const fetchTemplatesByType = async (type) => {
        setIsLoading(true);
        setCurrentIndex(0);
        try {
            const response = await apiService.getAll(`${config.GETALLWEBSITETEMPLATES}?type=${type}`);
            if (isMounted.current) {
                if (response && Array.isArray(response)) {
                    setTemplates(response);
                    if (response.length > 0) {
                        setSelectedTemplate(response[0]);
                    }
                } else if (response && response.data && Array.isArray(response.data)) {
                    setTemplates(response.data);
                    if (response.data.length > 0) {
                        setSelectedTemplate(response.data[0]);
                    }
                } else {
                    setTemplates([]);
                    setSelectedTemplate(null);
                }
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
            if (isMounted.current) {
                setTemplates([]);
                setSelectedTemplate(null);
            }
        } finally {
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    };

    const handleTypeChange = (type) => {
        setActiveType(type);
        setSelectedTemplate(null);
        setCurrentIndex(0);
    };

    const handlePreview = (template) => {
        setSelectedTemplate(template);
        setShowModal(true);
    };

    const handleUseTemplate = (templateId) => {
        if (onTemplateSelect) {
            onTemplateSelect(templateId);
        } else {
            window.location.href = `/register`;
        }
    };

    const handleThumbnailClick = (template, index) => {
        setSelectedTemplate(template);
        setCurrentIndex(index);
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="templates-display-wrapper" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Category Quick Selector - Pills */}
            <div className="d-flex justify-content-start gap-2 mb-3 flex-wrap">
                {TEMPLATE_TYPES.map((type) => (
                    <button
                        key={type.key}
                        onClick={() => handleTypeChange(type.key)}
                        className="d-flex align-items-center gap-2 px-3 py-2 border-0"
                        style={{
                            background: activeType === type.key ? `linear-gradient(135deg, ${DefaultData.THEMECOLOR1}, ${DefaultData.THEMECOLOR2})` : '#fff',
                            color: activeType === type.key ? '#fff' : '#6c757d',
                            borderRadius: '50px',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            boxShadow: activeType === type.key ? `0 5px 15px ${DefaultData.THEMECOLOR1}30` : '0 2px 5px rgba(0,0,0,0.05)',
                            fontSize: '0.85rem'
                        }}
                    >
                        <span>{type.icon}</span>
                        <span className="fw-bold">{type.label}</span>
                    </button>
                ))}
            </div>

            {/* Main Template Display */}
            <div style={{ flex: 1 }}>
            {isLoading ? (
                <div className="text-center py-5">
                    <div className="spinner-border" style={{ color: DefaultData.THEMECOLOR1 }} role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading templates...</p>
                </div>
            ) : templates.length > 0 && selectedTemplate ? (
                <div ref={scrollContainerRef} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div 
                        className="card border-0 shadow-lg overflow-hidden mb-3"
                        style={{ 
                            borderRadius: '16px',
                            transition: 'all 0.3s ease',
                            flex: 1
                        }}
                    >
                        {/* Image Container with Hover Overlay Buttons */}
                        <div 
                            className="position-relative"
                            style={{ 
                                height: '260px', 
                                overflow: 'hidden',
                                backgroundColor: '#000'
                            }}
                            onMouseEnter={() => setHoveredTemplate(selectedTemplate.templateId)}
                            onMouseLeave={() => setHoveredTemplate(null)}
                        >
                            {selectedTemplate.thumbnailUrl ? (
                                <img 
                                    src={selectedTemplate.thumbnailUrl} 
                                    alt={selectedTemplate.name}
                                    className="w-100 h-100"
                                    style={{ 
                                        objectFit: 'contain',
                                        objectPosition: 'center',
                                        backgroundColor: '#f8f9fa',
                                        transition: 'transform 0.5s ease',
                                        transform: hoveredTemplate === selectedTemplate.templateId ? 'scale(1.05)' : 'scale(1)'
                                    }}
                                    onError={(e) => {
                                        e.target.src = 'https://placehold.co/600x400/6366f1/ffffff?text=Template';
                                    }}
                                loading="lazy" />
                            ) : (
                                <div className="w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: DefaultData.THEMECOLOR3 || '#f3f4f6' }}>
                                    <span className="display-1">🎨</span>
                                </div>
                            )}
                            
                            {/* Hover Overlay with Buttons */}
                            <div 
                                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center gap-3"
                                style={{
                                    background: `linear-gradient(135deg, ${DefaultData.THEMECOLOR1}CC, ${DefaultData.THEMECOLOR2}CC)`,
                                    opacity: hoveredTemplate === selectedTemplate.templateId ? 1 : 0,
                                    transition: 'opacity 0.3s ease',
                                    backdropFilter: 'blur(2px)'
                                }}
                            >
                                <button 
                                    className="btn btn-light rounded-circle d-flex align-items-center justify-content-center"
                                    style={{ 
                                        width: '45px', 
                                        height: '45px', 
                                        borderRadius: '50%',
                                        transition: 'all 0.3s ease',
                                        border: 'none',
                                        boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
                                    }}
                                    onClick={() => handlePreview(selectedTemplate)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.1)';
                                        e.currentTarget.style.backgroundColor = DefaultData.THEMECOLOR1;
                                        e.currentTarget.style.color = '#fff';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                        e.currentTarget.style.backgroundColor = '#fff';
                                        e.currentTarget.style.color = DefaultData.THEMECOLOR1;
                                    }}
                                >
                                    <FaEye size={18} />
                                </button>
                                <button 
                                    className="btn btn-light rounded-circle d-flex align-items-center justify-content-center"
                                    style={{ 
                                        width: '45px', 
                                        height: '45px', 
                                        borderRadius: '50%',
                                        transition: 'all 0.3s ease',
                                        border: 'none',
                                        boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
                                    }}
                                    onClick={() => handleUseTemplate(selectedTemplate.templateId)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.1)';
                                        e.currentTarget.style.backgroundColor = DefaultData.THEMECOLOR1;
                                        e.currentTarget.style.color = '#fff';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                        e.currentTarget.style.backgroundColor = '#fff';
                                        e.currentTarget.style.color = DefaultData.THEMECOLOR1;
                                    }}
                                >
                                    <FaArrowRight size={18} />
                                </button>
                            </div>
                        </div>
                        
                        {/* Card Body */}
                        <div className="card-body p-3">
                            <h5 className="card-title fw-bold mb-1" style={{ color: DefaultData.THEMECOLOR2, fontSize: '0.95rem' }}>
                                {selectedTemplate.name}
                            </h5>
                            <p className="card-text text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                                {selectedTemplate.description?.substring(0, 70) || 'Modern and responsive design perfect for travel businesses.'}
                                {selectedTemplate.description?.length > 70 && '...'}
                            </p>
                            
                            <div className="d-flex gap-2 flex-wrap">
                                <span className="badge bg-light text-dark px-2 py-1" style={{ fontSize: '0.6rem' }}>
                                    <FaMobileAlt size={10} className="me-1" /> Responsive
                                </span>
                                <span className="badge bg-light text-dark px-2 py-1" style={{ fontSize: '0.6rem' }}>
                                    <FaTachometerAlt size={10} className="me-1" /> Fast
                                </span>
                                <span className="badge bg-light text-dark px-2 py-1" style={{ fontSize: '0.6rem' }}>
                                    <FaPaintBrush size={10} className="me-1" /> Customizable
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="text-center py-5">
                    <div className="display-1 mb-3">🎨</div>
                    <h5 className="mb-2" style={{ color: DefaultData.THEMECOLOR2 }}>No Templates Found</h5>
                    <p className="text-muted small">
                        We're working on adding more {activeType} templates. Check back soon!
                    </p>
                    <button 
                        className="btn btn-sm px-3 py-2"
                        style={{ 
                            backgroundColor: DefaultData.THEMECOLOR1,
                            color: '#fff',
                            borderRadius: '8px'
                        }}
                        onClick={() => window.location.href = '/contact'}
                    >
                        Request Custom Template
                    </button>
                </div>
            )}
            </div>

            {/* Thumbnails Strip Below */}
            {templates.length > 1 && (
                <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted fw-bold">All Templates</small>
                        <small className="text-muted">{templates.length} templates available</small>
                    </div>
                    <div 
                        ref={thumbnailScrollRef}
                        className="d-flex gap-2 overflow-auto"
                        style={{ 
                            scrollbarWidth: 'thin',
                            msOverflowStyle: 'auto',
                            overflowX: 'auto',
                            paddingBottom: '8px'
                        }}
                    >
                        {templates.map((template, index) => (
                            <div 
                                key={template.templateId || index}
                                className="flex-shrink-0"
                                style={{ width: '65px', cursor: 'pointer' }}
                                onClick={() => handleThumbnailClick(template, index)}
                            >
                                <div 
                                    className="position-relative rounded overflow-hidden"
                                    style={{ 
                                        height: '45px',
                                        border: selectedTemplate?.templateId === template.templateId ? `2px solid ${DefaultData.THEMECOLOR1}` : '2px solid transparent',
                                        borderRadius: '8px',
                                        transition: 'all 0.2s ease',
                                        opacity: selectedTemplate?.templateId === template.templateId ? 1 : 0.7,
                                        transform: selectedTemplate?.templateId === template.templateId ? 'scale(1.02)' : 'scale(1)'
                                    }}
                                >
                                    {template.thumbnailUrl ? (
                                        <img 
                                            src={template.thumbnailUrl} 
                                            alt={template.name}
                                            className="w-100 h-100"
                                            style={{ objectFit: 'cover' }}
                                        loading="lazy" />
                                    ) : (
                                        <div className="w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#e9ecef' }}>
                                            <span className="fs-6">🎨</span>
                                        </div>
                                    )}
                                    
                                    {selectedTemplate?.templateId === template.templateId && (
                                        <div 
                                            className="position-absolute bottom-0 start-0 w-100"
                                            style={{ 
                                                height: '2px', 
                                                backgroundColor: DefaultData.THEMECOLOR1,
                                                bottom: 0
                                            }}
                                        ></div>
                                    )}
                                </div>
                                <p className="text-center small mt-1 mb-0" style={{ 
                                    fontSize: '0.55rem',
                                    fontWeight: selectedTemplate?.templateId === template.templateId ? 'bold' : 'normal',
                                    color: selectedTemplate?.templateId === template.templateId ? DefaultData.THEMECOLOR1 : '#6c757d'
                                }}>
                                    {template.name.length > 10 ? template.name.substring(0, 8) + '...' : template.name}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {showModal && selectedTemplate && (
                <div 
                    className="modal show d-block" 
                    tabIndex="-1" 
                    style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050 }}
                    onClick={() => setShowModal(false)}
                >
                    <div className="modal-dialog modal-xl modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                            <div className="modal-header border-0 p-4">
                                <div>
                                    <h3 className="mb-1" style={{ color: DefaultData.THEMECOLOR2 }}>{selectedTemplate.name}</h3>
                                    <span className="badge px-3 py-2" style={{ backgroundColor: DefaultData.THEMECOLOR1, color: '#fff' }}>
                                        {selectedTemplate.templateType}
                                    </span>
                                </div>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body p-0">
                                {selectedTemplate.thumbnailUrl ? (
                                    <img 
                                        src={selectedTemplate.thumbnailUrl} 
                                        alt={selectedTemplate.name}
                                        className="w-100"
                                        style={{ maxHeight: '70vh', objectFit: 'contain' }}
                                    loading="lazy" />
                                ) : (
                                    <div className="p-5 text-center bg-light">
                                        <div className="display-1">🎨</div>
                                        <p className="mt-3">Template preview coming soon</p>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer border-0 p-4">
                                <button 
                                    className="btn px-4 py-2"
                                    style={{ 
                                        backgroundColor: '#f3f4f6',
                                        color: DefaultData.THEMECOLOR2,
                                        borderRadius: '10px'
                                    }}
                                    onClick={() => setShowModal(false)}
                                >
                                    Close
                                </button>
                                <button 
                                    className="btn px-4 py-2"
                                    style={{ 
                                        backgroundColor: DefaultData.THEMECOLOR1,
                                        color: '#fff',
                                        borderRadius: '10px'
                                    }}
                                    onClick={() => {
                                        setShowModal(false);
                                        handleUseTemplate(selectedTemplate.templateId);
                                    }}
                                >
                                    Use This Template <FaArrowRight className="ms-2" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

// Suppliers Display Component
const SuppliersDisplay = memo(() => {
    const supplierLogos = {
        tripjack: "/assets/images/suppliers/tripjack.png",
        tbo: "/assets/images/suppliers/tbo.png",
        akbar: "/assets/images/suppliers/akbar.png",
        rezlive: "/assets/images/suppliers/rezlive.png",
        grnconnect: "/assets/images/suppliers/grn.png",
        riya: "/assets/images/suppliers/Riya.png",
        viator: "/assets/images/suppliers/viator.png",
        airiq: "/assets/images/suppliers/airiq.png",
        zotrai: "/assets/images/suppliers/ZotraiLogo.png",
        eTravelSmart: "/assets/images/suppliers/eTravelSmart.png"
    };

    const brandColors = {
        tripjack: "#2563eb",
        tbo: "#FF4B4B",
        akbar: "#00A859",
        rezlive: "#6B46C1",
        grnconnect: "#2E7D32",
        riya: "#9C27B0",
        viator: "#E31B23",
        airiq: "#0284C7",
        zotrai: "#FF7F00"
    };

    const suppliers = [
        { key: 'tripjack', name: 'Tripjack', logo: supplierLogos.tripjack, color: brandColors.tripjack, features: ['Flights', 'Hotels', 'Packages'] },
        { key: 'tbo', name: 'TBO', logo: supplierLogos.tbo, color: brandColors.tbo, features: ['Flights', 'Hotels', 'Holidays'] },
        { key: 'akbar', name: 'Akbar Travels', logo: supplierLogos.akbar, color: brandColors.akbar, features: ['Flights', 'Hotels', 'Umrah'] },
        { key: 'rezlive', name: 'RezLive', logo: supplierLogos.rezlive, color: brandColors.rezlive, features: ['Hotels', 'Transfers', 'Activities'] },
        { key: 'grnconnect', name: 'GRN Connect', logo: supplierLogos.grnconnect, color: brandColors.grnconnect, features: ['Hotels', 'Sightseeing'] },
        { key: 'riya', name: 'Riya Travel', logo: supplierLogos.riya, color: brandColors.riya, features: ['Flights', 'Hotels', 'Visa'] },
        { key: 'viator', name: 'Viator', logo: supplierLogos.viator, color: brandColors.viator, features: ['Activities', 'Tours', 'Attractions'] },
        { key: 'airiq', name: 'Air iQ', logo: supplierLogos.airiq, color: brandColors.airiq, features: ['Flights', 'API Integration'] }
    ];

    return (
        <div className="suppliers-display-wrapper" style={{ height: '100%' }}>
            <div className="suppliers-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: '1rem'
            }}>
                {suppliers.map((supplier) => (
                    <div
                        key={supplier.key}
                        className="supplier-card"
                        style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: '1rem',
                            textAlign: 'center',
                            border: `1px solid ${supplier.color}30`,
                            transition: 'all 0.3s ease',
                            cursor: 'default',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = `0 12px 20px -8px ${supplier.color}40`;
                            e.currentTarget.style.borderColor = supplier.color;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                            e.currentTarget.style.borderColor = `${supplier.color}30`;
                        }}
                    >
                        <div className="supplier-logo-wrapper" style={{
                            width: '70px',
                            height: '70px',
                            margin: '0 auto 0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f8f9fa',
                            borderRadius: '12px',
                            padding: '0.5rem'
                        }}>
                            <img
                                src={supplier.logo}
                                alt={supplier.name}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain'
                                }}
                                onError={(e) => {
                                    e.target.src = `https://placehold.co/100x100/${supplier.color.replace('#', '')}/ffffff?text=${supplier.name.charAt(0)}`;
                                }}
                            loading="lazy" />
                        </div>
                        <h6 style={{ marginBottom: '0.5rem', fontWeight: '700', color: '#1e293b' }}>{supplier.name}</h6>
                        <div className="supplier-features" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {supplier.features.map((feature, idx) => (
                                <span key={idx} style={{
                                    fontSize: '0.65rem',
                                    background: `${supplier.color}15`,
                                    color: supplier.color,
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '20px',
                                    fontWeight: '500'
                                }}>{feature}</span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <div className="text-center mt-4">
                <p className="text-muted small">and 50+ more suppliers ready to connect</p>
            </div>
        </div>
    );
});

const PortalSetUpSteps = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [templatesKey, setTemplatesKey] = useState(0);
  const [currentView, setCurrentView] = useState('suppliers');
  const [showPricingDetails, setShowPricingDetails] = useState(false);
  
  // Ref for scrolling to pricing section
  const pricingSectionRef = useRef(null);

  const companyInfo = {
    name: "Zotrai",
    themeColor: "#FF7F00",
    email: "support@zotrai.com"
  };

  const supplierLogos = {
    tripjack: "/assets/images/suppliers/tripjack.png",
    tbo: "/assets/images/suppliers/tbo.png",
    akbar: "/assets/images/suppliers/akbar.png",
    rezlive: "/assets/images/suppliers/rezlive.png",
    grnconnect: "/assets/images/suppliers/grn.png",
    riya: "/assets/images/suppliers/Riya.png",
    viator: "/assets/images/suppliers/viator.png",
    airiq: "/assets/images/suppliers/airiq.png",
    zotrai: "/assets/images/suppliers/ZotraiLogo.png",
    eTravelSmart: "/assets/images/suppliers/eTravelSmart.png"
  };

  const brandColors = {
    tripjack: "#2563eb",
    tbo: "#FF4B4B",
    akbar: "#00A859",
    rezlive: "#6B46C1",
    grnconnect: "#2E7D32",
    riya: "#9C27B0",
    viator: "#E31B23",
    airiq: "#0284C7",
    zotrai: "#FF7F00"
  };

  const steps = [
    {
      number: 1,
      title: "Choose Your Supplier",
      shortTitle: "Connect",
      icon: "🔑",
      description: "Connect directly to major travel suppliers",
      action: "Login → Paste Keys → Auto-connect",
      time: "2 mins",
      color: "#FF7F00",
      bgColor: "#FFF3E8",
      details: [ "Supports 50+ suppliers", "Secure & encrypted"],
      buttonText: "Explore Suppliers",
      viewName: "suppliers"
    },
    {
      number: 2,
      title: "Choose Template",
      shortTitle: "Design",
      icon: "🎨",
      description: "Pick your brand style & layout",
      action: "Select Template → Customize → Preview",
      time: "5 mins",
      color: "#FF7F00",
      bgColor: "#FFF3E8",
      details: ["100+ ready templates", "Drag & drop editor", "Live preview"],
      buttonText: "Browse Templates",
      viewName: "templates"
    },
    {
      number: 3,
      title: "Launch Portal",
      shortTitle: "Go Live",
      icon: "🚀",
      description: "Start accepting bookings instantly",
      action: "One Click → Deploy → Done!",
      time: "1 min",
      color: "#FF7F00",
      bgColor: "#FFF3E8",
      details: ["Instant deployment", "Custom domain ready", "24/7 support"],
      buttonText: "Get Started",
      viewName: "pricing"
    }
  ];

  const handleStepButtonClick = (stepNumber, viewName) => {
    if (stepNumber === 1) {
      setCurrentView('suppliers');
      setShowPricingDetails(false);
      const contentSection = document.getElementById('step-content-section');
      if (contentSection) {
        contentSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (stepNumber === 2) {
      setCurrentView('templates');
      setShowPricingDetails(false);
      setTemplatesKey(prev => prev + 1);
      const contentSection = document.getElementById('step-content-section');
      if (contentSection) {
        contentSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (stepNumber === 3) {
      // Toggle pricing details and scroll to pricing section
      setShowPricingDetails(!showPricingDetails);
      setShowSuccess(false);
      // Scroll to pricing section when clicked
      if (pricingSectionRef.current) {
        pricingSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Step Content Section - Shows either Suppliers or Templates based on currentView
  const StepContentSection = () => (
    <section id="step-content-section" className={styles.stepContentSection}>
      <div className={styles.stepContentHeader}>
        <div>
          <h3>
            {currentView === 'suppliers' ? '🔌 Connect Your Travel Suppliers' : '🎨 Choose Your Website Template'}
          </h3>
          <p>
            {currentView === 'suppliers' 
              ? 'Select from 50+ integrated travel suppliers and connect with API keys' 
              : 'Browse through our collection of professionally designed templates'}
          </p>
        </div>
        <button
          onClick={() => {
            if (currentView === 'suppliers') {
              setCurrentView('templates');
            } else {
              setCurrentView('suppliers');
            }
          }}
          className={styles.switchButton}
        >
          {currentView === 'suppliers' ? 'Skip to Templates →' : '← Back to Suppliers'}
        </button>
      </div>
      
      {currentView === 'suppliers' ? <SuppliersDisplay /> : <TemplatesDisplay key={templatesKey} onTemplateSelect={(id) => console.log('Template selected:', id)} />}
    </section>
  );

  // Pricing Details Component - shown when clicking on Get Started button
  const PricingDetails = () => (
    <div className={styles.pricingDetails}>
      <div className={styles.pricingDetailsHeader}>
        <span>🎉</span>
        <h3>Launch Offer</h3>
      </div>
      <p className={styles.pricingDetailsText}>
        Get everything you need to start your travel business:
      </p>
      <div className={styles.pricingDetailsGrid}>
        <div className={styles.pricingDetailsItem}><span>✅</span> Custom Domain Included</div>
        <div className={styles.pricingDetailsItem}><span>✅</span> Free SSL Certificate</div>
        <div className={styles.pricingDetailsItem}><span>✅</span> 100+ Professional Templates</div>
        <div className={styles.pricingDetailsItem}><span>✅</span> 50+ Supplier Integrations</div>
        <div className={styles.pricingDetailsItem}><span>✅</span> 24/7 Customer Support</div>
        <div className={styles.pricingDetailsItem}><span>✅</span> No Setup Fee</div>
      </div>
      <div className={styles.pricingDetailsButtons}>
        <Link to="/register" className={styles.pricingDetailsLink}>
          Get Started Now →
        </Link>
        <button onClick={() => setShowPricingDetails(false)} className={styles.pricingDetailsClose}>
          Close
        </button>
      </div>
    </div>
  );

  const PricingCard = () => (
    <div ref={pricingSectionRef} className={styles.pricingCard}>
      <div className={styles.pricingContent}>
        <div className={styles.pricingLeft}>
          <div className={styles.pricingBadge}>LAUNCH OFFER</div>
          <h3 className={styles.pricingCardTitle}>Start Your Travel Business Today</h3>
          <p className={styles.pricingDescription}>
            Complete website solution with all supplier integrations included
          </p>
          <div className={styles.pricingFeatures}>
            <span><i className="ri-checkbox-circle-fill" style={{ color: "#10B981" }}></i> Custom domain</span>
            <span><i className="ri-checkbox-circle-fill" style={{ color: "#10B981" }}></i> SSL security</span>
            <span><i className="ri-checkbox-circle-fill" style={{ color: "#10B981" }}></i> 100+ templates</span>
            <span><i className="ri-checkbox-circle-fill" style={{ color: "#10B981" }}></i> All suppliers</span>
            <span><i className="ri-checkbox-circle-fill" style={{ color: "#10B981" }}></i> 24/7 support</span>
          </div>
        </div>
        <div className={styles.pricingRight}>
          <button 
            onClick={() => {
              setShowPricingDetails(!showPricingDetails);
              // Scroll to pricing section when button is clicked
              if (pricingSectionRef.current) {
                pricingSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            className={styles.pricingCta}
          >
            Get Started <i className="ri-arrow-right-line"></i>
          </button>
          <p className={styles.pricingNote}>No setup fee • Cancel anytime</p>
        </div>
      </div>
      {showPricingDetails && <PricingDetails />}
    </div>
  );

  return (
    <>
      <div className={styles.container}>
        <div className={styles.mainCard}>
          
          <div className={styles.header}>
            <div className={styles.badge}>
              <span>⚡ ZOTRAI • TRAVEL PLATFORM</span>
            </div>
            <h1 className={styles.title}>
              Launch Your Travel Portal in 
              <span> Under 10 Minutes</span>
            </h1>
            <p className={styles.subtitle}>
             No complex setup. Just 3 simple steps to start selling flights, hotels & packages.
            </p>
          </div>

          <div className={styles.stepsWrapper}>
            <div className={styles.connectingLine}></div>
            
            <div className={styles.stepsGrid}>
              {steps.map((step) => (
                <div 
                  key={step.number}
                  className={styles.stepCard}
                >
                  <div className={styles.stepCircle}>
                    <span>{step.icon}</span>
                  </div>

                  <h3 className={styles.stepTitle}>{step.title}</h3>
                  <p className={styles.stepDesc}>{step.description}</p>
                  
                  <div className={styles.actionBadge}>
                    <span>{step.action}</span>
                  </div>

                  <div className={styles.timeBadge}>
                    <span>⏱️ {step.time}</span>
                  </div>

                  {/* Step Button */}
                  <button
                    onClick={() => handleStepButtonClick(step.number, step.viewName)}
                    className={styles.stepButton}
                  >
                    {step.buttonText} →
                  </button>

                  <div className={styles.stepDetails}>
                    {step.details.map((detail, i) => (
                      <div key={i} className={styles.detailItem}>
                        <span>✓</span> {detail}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
           
          {/* Step Content Section - Shows Suppliers or Templates based on selection */}
          <StepContentSection />
          
          <PricingCard />

          <div className={styles.comparisonSection}>
            <div className={styles.comparisonGrid}>
              <div className={styles.comparisonCard}>
                <div className={styles.comparisonIcon}>😰</div>
                <h4>Traditional Setup</h4>
                <ul className={styles.comparisonList}>
                  <li>❌ Weeks of development</li>
                  <li>❌ Expensive integration costs</li>
                  <li>❌ Multiple API connections</li>
                  <li>❌ Technical team required</li>
                </ul>
              </div>
              
              <div className={styles.vsBadge}>VS</div>
              
              <div className={`${styles.comparisonCard} ${styles.comparisonCardWinner}`}>
                <div className={styles.comparisonIcon}>⚡</div>
                <h4 style={{ color: companyInfo.themeColor }}>With Zotrai</h4>
                <ul className={styles.comparisonList}>
                  <li>✅ <strong>10 minutes setup</strong></li>
                  <li>✅ <strong>Zero integration fees</strong></li>
                  <li>✅ <strong>Single API, all suppliers</strong></li>
                  <li>✅ <strong>No tech skills needed</strong></li>
                </ul>
              </div>
            </div>
          </div>

          <div className={styles.liveDemoSection}>
            <div className={styles.liveDemoHeader}>
              <span className={styles.liveDot}></span>
              <span>LIVE DEMO — Watch how simple it is</span>
            </div>
            
            <div className={styles.demoFlow}>
              <div className={styles.demoStep}>
                <div className={styles.demoIcon}>1️⃣</div>
                <div className={styles.demoText}>Login</div>
                <span className={styles.demoArrow}>→</span>
              </div>
              <div className={styles.demoStep}>
                <div className={styles.demoIcon}>2️⃣</div>
                <div className={styles.demoText}>Choose Supplier</div>
                <span className={styles.demoArrow}>→</span>
              </div>
              <div className={styles.demoStep}>
                <div className={styles.demoIcon}>3️⃣</div>
                <div className={styles.demoText}>Choose template</div>
                <span className={styles.demoArrow}>→</span>
              </div>
              <div className={styles.demoStep}>
                <div className={styles.demoIcon}>4️⃣</div>
                <div className={styles.demoText}>Click Launch!</div>
                <span className={styles.demoArrow}>🎉</span>
              </div>
            </div>
            
          <Link to="/register" className={styles.ctaButton}>
  Start Your Free Trial →
  <span style={{ fontSize: '18px', marginLeft: '8px' }}>⚡</span>
</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default PortalSetUpSteps;